/**
 * AutoDrive Theme — Main JS
 * Handles: announcement bar, sticky header, mobile nav,
 *          scroll-to-top, FAQ accordion, product tabs,
 *          lazy images (fallback), toast notifications.
 */

(function () {
  'use strict';

  /* ============================================================
     UTILITY
     ============================================================ */
  function qs(selector, context) {
    return (context || document).querySelector(selector);
  }

  function qsa(selector, context) {
    return Array.from((context || document).querySelectorAll(selector));
  }

  function on(el, event, handler, options) {
    if (el) el.addEventListener(event, handler, options);
  }

  /**
   * Show a toast notification.
   * @param {string} message
   * @param {'success'|'error'|''} type
   * @param {number} duration  ms
   */
  window.showToast = function (message, type, duration) {
    type = type || '';
    duration = duration || 3000;

    var existing = qs('.toast');
    if (existing) existing.remove();

    var toast = document.createElement('div');
    toast.className = 'toast' + (type ? ' toast--' + type : '');
    toast.textContent = message;
    document.body.appendChild(toast);

    // Trigger animation
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        toast.classList.add('is-visible');
      });
    });

    setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () { toast.remove(); }, 400);
    }, duration);
  };

  /* ============================================================
     ANNOUNCEMENT BAR DISMISS
     ============================================================ */
  function initAnnouncementBar() {
    var bar = qs('[data-announcement-bar]');
    var closeBtn = qs('[data-announcement-close]');
    if (!bar || !closeBtn) return;

    var key = 'autodrive_announcement_dismissed';
    if (localStorage.getItem(key)) {
      bar.style.display = 'none';
      return;
    }

    on(closeBtn, 'click', function () {
      bar.style.height = bar.offsetHeight + 'px';
      bar.style.overflow = 'hidden';
      requestAnimationFrame(function () {
        bar.style.transition = 'height 0.3s ease';
        bar.style.height = '0';
      });
      setTimeout(function () {
        bar.style.display = 'none';
      }, 320);
      localStorage.setItem(key, '1');
    });
  }

  /* ============================================================
     STICKY HEADER — hide on scroll down, show on scroll up
     ============================================================ */
  function initStickyHeader() {
    // Header stays fixed — hide-on-scroll disabled
  }

  /* ============================================================
     MOBILE NAV DRAWER
     ============================================================ */
  function initMobileNav() {
    var openBtn   = qs('[data-mobile-nav-open]');
    var closeBtn  = qs('[data-mobile-nav-close]');
    var drawer    = qs('[data-mobile-nav-drawer]');
    var overlay   = qs('[data-mobile-nav-overlay]');

    if (!openBtn || !drawer) return;

    function openNav() {
      drawer.classList.add('is-open');
      if (overlay) overlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
      closeBtn && closeBtn.focus();
    }

    function closeNav() {
      drawer.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-visible');
      document.body.style.overflow = '';
      openBtn.focus();
    }

    on(openBtn, 'click', openNav);
    on(closeBtn, 'click', closeNav);
    on(overlay, 'click', closeNav);

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeNav();
      }
    });
  }

  /* ============================================================
     SCROLL TO TOP BUTTON
     ============================================================ */
  function initScrollTop() {
    var btn = qs('[data-scroll-top]');
    if (!btn) return;

    window.addEventListener('scroll', function () {
      btn.classList.toggle('is-visible', window.scrollY > 400);
    }, { passive: true });

    on(btn, 'click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /* ============================================================
     FAQ ACCORDION
     ============================================================ */
  function initAccordions() {
    qsa('[data-accordion-trigger]').forEach(function (trigger) {
      var item = trigger.closest('[data-accordion-item]');
      var content = item && item.querySelector('[data-accordion-content]');

      on(trigger, 'click', function () {
        var isOpen = item.classList.contains('is-open');

        // Close all siblings
        var parent = item.parentElement;
        qsa('[data-accordion-item]', parent).forEach(function (sibling) {
          sibling.classList.remove('is-open');
        });

        if (!isOpen) {
          item.classList.add('is-open');
        }
      });
    });
  }

  /* ============================================================
     PRODUCT TABS
     ============================================================ */
  function initTabs() {
    qsa('[data-tabs]').forEach(function (tabsEl) {
      var triggers = qsa('[data-tab-trigger]', tabsEl);
      var panels   = qsa('[data-tab-panel]', tabsEl.closest('[data-tabs-container]') || document);

      triggers.forEach(function (trigger) {
        on(trigger, 'click', function () {
          var target = trigger.dataset.tabTrigger;

          triggers.forEach(function (t) { t.classList.remove('is-active'); });
          panels.forEach(function (p) { p.classList.remove('is-active'); });

          trigger.classList.add('is-active');
          var panel = qs('[data-tab-panel="' + target + '"]');
          if (panel) panel.classList.add('is-active');
        });
      });
    });
  }

  /* ============================================================
     FILTER GROUP TOGGLES
     ============================================================ */
  function initFilterGroups() {
    qsa('[data-filter-group]').forEach(function (group) {
      var trigger = qs('[data-filter-group-trigger]', group);
      if (!trigger) return;

      // Open first group by default
      group.classList.add('is-open');

      on(trigger, 'click', function () {
        group.classList.toggle('is-open');
      });
    });
  }

  /* ============================================================
     MOBILE FILTER DRAWER
     ============================================================ */
  function initMobileFilterDrawer() {
    var openBtn  = qs('[data-filter-drawer-open]');
    var closeBtn = qs('[data-filter-drawer-close]');
    var drawer   = qs('[data-filter-drawer]');
    var overlay  = qs('[data-filter-drawer-overlay]');

    if (!openBtn || !drawer) return;

    function open() {
      drawer.classList.add('is-open');
      if (overlay) overlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    }

    function close() {
      drawer.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-visible');
      document.body.style.overflow = '';
    }

    on(openBtn, 'click', open);
    on(closeBtn, 'click', close);
    on(overlay, 'click', close);
  }

  /* ============================================================
     PRODUCT IMAGE GALLERY (thumbnail clicks)
     ============================================================ */
  function initProductGallery() {
    var thumbs   = qsa('[data-product-thumb]');
    var mainImg  = qs('[data-product-main-img]');

    thumbs.forEach(function (thumb) {
      on(thumb, 'click', function () {
        var src    = thumb.dataset.src;
        var srcset = thumb.dataset.srcset;

        thumbs.forEach(function (t) {
          t.closest('[data-product-thumb-wrapper]') &&
            t.closest('[data-product-thumb-wrapper]').classList.remove('is-active');
          t.classList.remove('is-active');
        });

        var wrapper = thumb.closest('.product-media__thumb');
        if (wrapper) wrapper.classList.add('is-active');
        thumb.classList.add('is-active');

        if (mainImg && src) {
          mainImg.src = src;
          if (srcset) mainImg.srcset = srcset;
        }
      });
    });
  }

  /* ============================================================
     MOBILE SEARCH PANEL — slide-down, no page navigation
     ============================================================ */
  function initSearchExpand() {
    var toggleBtn = qs('[data-search-toggle]');
    var panel     = qs('[data-mobile-search-panel]');
    var closeBtn  = qs('[data-mobile-search-close]');

    if (!toggleBtn || !panel) return;

    function openPanel() {
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      toggleBtn.setAttribute('aria-expanded', 'true');
      // Small delay so the transition is visible before focus
      setTimeout(function () {
        var input = panel.querySelector('[data-mobile-search-input]');
        if (input) input.focus();
      }, 80);
    }

    function closePanel() {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      toggleBtn.setAttribute('aria-expanded', 'false');
      toggleBtn.focus();
    }

    on(toggleBtn, 'click', function () {
      panel.classList.contains('is-open') ? closePanel() : openPanel();
    });

    on(closeBtn, 'click', closePanel);

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && panel.classList.contains('is-open')) closePanel();
    });

    // Close when tapping outside the header
    document.addEventListener('click', function (e) {
      if (!e.target.closest('.site-header') && panel.classList.contains('is-open')) {
        closePanel();
      }
    });
  }

  /* ============================================================
     NEWSLETTER FORM — basic UX feedback
     ============================================================ */
  function initNewsletterForm() {
    qsa('#newsletter-form').forEach(function (form) {
      on(form, 'submit', function () {
        // Shopify handles the actual POST; we just add loading state
        var btn = form.querySelector('[type="submit"]');
        if (btn) btn.classList.add('btn--loading');
      });
    });
  }

  /* ============================================================
     INIT
     ============================================================ */
  document.addEventListener('DOMContentLoaded', function () {
    initAnnouncementBar();
    initStickyHeader();
    initMobileNav();
    initScrollTop();
    initAccordions();
    initTabs();
    initFilterGroups();
    initMobileFilterDrawer();
    initProductGallery();
    initSearchExpand();
    initNewsletterForm();
  });

})();
